import type { RepeatingListColumn } from '../types/assessmentQuestion';

import { dateErrorMessage, numberErrorMessage, type Translate } from './assessmentErrorMessages';
import { OPEN_MIN, validateAssessmentDate } from './validateAssessmentDate';
import { validateAssessmentNumber } from './validateAssessmentNumber';

/**
 * Validates one repeating-list cell against its column's `cellType`, or
 * `undefined` when it's valid.
 *
 * A blank cell is only an error when `options.required` says so — see
 * `evaluateRepeatingListAnswer`, the only caller that ever sets it: a
 * required list's ROW, once the member has started it (any cell in it is
 * non-blank), must be filled in completely (Alen, Sep 3 — "if a user starts
 * a row, all fields in that row become required"). An untouched row stays
 * fully optional, and blur-time validation (`useRepeatingListRows.validateRowCell`)
 * never passes `required` at all, matching how every other required field in
 * this module only surfaces its "required" error on Continue, not on blur.
 */
export function cellErrorMessage(
  column: RepeatingListColumn,
  rawValue: string,
  t: Translate,
  options?: { required?: boolean }
): string | undefined {
  const trimmed = rawValue.trim();
  if (trimmed === '') return options?.required ? t('Enter a value.') : undefined;
  if (column.cellType === 'date') {
    const validation = validateAssessmentDate(trimmed, {
      // Unlike the standalone date question (where "no authored floor" means
      // Jan 1 of last year — a sensible default for a forward-looking
      // question), a repeating-list date column is routinely historical
      // (e.g. a medication's start date). Only bound the floor when the
      // column authors one explicitly; otherwise open it all the way rather
      // than inheriting validateAssessmentDate's forward-looking default —
      // matching the number cell path below, which applies no default bound
      // of its own either.
      min: column.minDate ?? OPEN_MIN,
      max: column.maxDate,
    });
    return validation.isValid ? undefined : dateErrorMessage(validation, t);
  }
  if (column.cellType === 'number') {
    const validation = validateAssessmentNumber(trimmed, {
      min: column.minNumber,
      max: column.maxNumber,
    });
    return validation.isValid ? undefined : numberErrorMessage(validation, t);
  }
  return undefined;
}

/**
 * The value actually committed for one repeating-list cell, once it's already
 * passed `cellErrorMessage` above. A `'date'` cell commits the parsed ISO
 * `YYYY-MM-DD` — the same normalized shape the standalone date question
 * commits via `checkDateAnswer` — rather than whatever the display mask
 * produced (`"03/14/2024"`), so the same authored `cellType: "date"` always
 * reaches the server in one wire format regardless of which surface collected
 * it. Every other cell type commits the trimmed display value unchanged.
 */
export function normalizedCellValue(column: RepeatingListColumn, trimmedValue: string): string {
  if (trimmedValue === '' || column.cellType !== 'date') return trimmedValue;
  const validation = validateAssessmentDate(trimmedValue, {
    min: column.minDate ?? OPEN_MIN,
    max: column.maxDate,
  });
  // Only a cell that already passed cellErrorMessage reaches here (see
  // evaluateRepeatingListAnswer's hasCellErrors guard), so normalizedDate is
  // never null in practice; the fallback just keeps this total.
  return validation.normalizedDate ?? trimmedValue;
}
